import { Box, Divider, FormHelperText, Typography } from "@mui/material"
import type { ReactElement } from "react"
import { useForm } from "react-hook-form"

import { SearchFilterDrawer } from "@/components/common/SearchFilterDrawer/SearchFilterDrawer"
import { AerosCheckbox, AerosDatePicker, AerosRadioGroup } from "@/components/UiKit"

export interface StatusDateRangeSearchParams<TStatus extends string, TDateRangeType extends string> {
  statuses?: TStatus[]
  dateRangeType?: TDateRangeType
  dateFrom?: string
  dateTo?: string
}

interface StatusDateRangeFilterDrawerProps<
  TStatus extends string,
  TDateRangeType extends string,
  TSearchParams extends StatusDateRangeSearchParams<TStatus, TDateRangeType>,
> {
  open: boolean
  searchParams: TSearchParams
  onToggle: () => void
  onChange: (updates: Partial<TSearchParams>) => void
  onClear: () => void
  statusOptions: { value: TStatus; label: string }[]
  dateRangeTypeOptions: { value: TDateRangeType; label: string }[]
  testIdPrefix: string
}

interface FilterFormValues<TStatus extends string, TDateRangeType extends string> {
  statuses: TStatus[]
  dateRangeType?: TDateRangeType
  dateFrom?: string
  dateTo?: string
}

export const StatusDateRangeFilterDrawer = <
  TStatus extends string,
  TDateRangeType extends string,
  TSearchParams extends StatusDateRangeSearchParams<TStatus, TDateRangeType>,
>({
  open,
  searchParams,
  onToggle,
  onChange,
  onClear,
  statusOptions,
  dateRangeTypeOptions,
  testIdPrefix,
}: StatusDateRangeFilterDrawerProps<TStatus, TDateRangeType, TSearchParams>): ReactElement => {
  const { statuses = [], dateRangeType, dateFrom, dateTo } = searchParams

  const { watch, setValue } = useForm<FilterFormValues<TStatus, TDateRangeType>>({
    values: { statuses, dateRangeType, dateFrom, dateTo },
    mode: "onChange",
  })

  const watchedDateRangeType = watch("dateRangeType")
  const watchedDateFrom = watch("dateFrom")
  const watchedDateTo = watch("dateTo")
  const isDateRangeInvalid = !!watchedDateFrom && !!watchedDateTo && watchedDateTo < watchedDateFrom
  const isDateFromRequired = !!watchedDateRangeType
  const isDateToRequired = !!watchedDateRangeType
  const isDateFromMissing = isDateFromRequired && !watchedDateFrom
  const isDateToMissing = isDateToRequired && !watchedDateTo

  const handleStatusChange = (status: TStatus) => {
    const current = watch("statuses") ?? []
    const updated = current.includes(status) ? current.filter((existing) => existing !== status) : [...current, status]
    setValue("statuses", updated)
    onChange({ statuses: updated } as Partial<TSearchParams>)
  }

  // Only commits once dateRangeType + dateFrom + dateTo are all set (or all cleared) — a partial
  // triple would otherwise trigger a fetch with an incomplete/silently-defaulted filter (AK-6853).
  const commitDateRangeIfComplete = (next: { dateRangeType?: TDateRangeType; dateFrom?: string; dateTo?: string }) => {
    if (next.dateFrom && next.dateTo && next.dateTo < next.dateFrom) return

    const isComplete = !!next.dateRangeType && !!next.dateFrom && !!next.dateTo
    const isFullyCleared = !next.dateRangeType && !next.dateFrom && !next.dateTo
    if (!isComplete && !isFullyCleared) return

    onChange({
      dateRangeType: next.dateRangeType,
      dateFrom: next.dateFrom,
      dateTo: next.dateTo,
    } as Partial<TSearchParams>)
  }

  const handleDateRangeTypeChange = (value: TDateRangeType) => {
    setValue("dateRangeType", value)
    commitDateRangeIfComplete({ dateRangeType: value, dateFrom: watch("dateFrom"), dateTo: watch("dateTo") })
  }

  const handleDateFromChange = (value: string | null) => {
    setValue("dateFrom", value ?? undefined)
    commitDateRangeIfComplete({
      dateRangeType: watch("dateRangeType"),
      dateFrom: value ?? undefined,
      dateTo: watch("dateTo"),
    })
  }

  const handleDateToChange = (value: string | null) => {
    setValue("dateTo", value ?? undefined)
    commitDateRangeIfComplete({
      dateRangeType: watch("dateRangeType"),
      dateFrom: watch("dateFrom"),
      dateTo: value ?? undefined,
    })
  }

  return (
    <SearchFilterDrawer open={open} toggleDrawer={onToggle} clear={onClear} testId={testIdPrefix} width={420}>
      <Box className='h-full min-w-80'>
        <Box className='flex flex-1 flex-col'>
          <Typography variant='subtitle1' className='px-4 py-1.5 font-bold text-gray-700'>
            Status
          </Typography>
          <Divider />
          <Box className='flex-1 overflow-y-auto overflow-x-hidden px-8'>
            {statusOptions.map(({ value, label }) => (
              <AerosCheckbox
                key={value}
                checked={watch("statuses")?.includes(value) ?? false}
                onChange={() => handleStatusChange(value)}
                label={label}
                className='w-full'
                data-testid={`${testIdPrefix}.status-checkbox-${value}`}
              />
            ))}
          </Box>
        </Box>

        <Box className='flex flex-1 flex-col'>
          <Typography variant='subtitle1' className='px-4 py-1.5 font-bold text-gray-700'>
            Date Range
          </Typography>
          <Divider />
          <Box className='flex-1 overflow-y-auto overflow-x-hidden px-8'>
            <Box className='flex flex-col gap-2 py-2'>
              <AerosRadioGroup
                options={dateRangeTypeOptions}
                value={watch("dateRangeType") ?? ""}
                onChange={handleDateRangeTypeChange}
                data-testid={`${testIdPrefix}.date-range-type-radio`}
              />
            </Box>
            <Box className='flex flex-col gap-2 pb-4'>
              <Box className='flex gap-4'>
                <Box className='flex-1'>
                  <AerosDatePicker
                    label='From'
                    required={isDateFromRequired}
                    value={watch("dateFrom") ?? null}
                    onChange={handleDateFromChange}
                    error={isDateRangeInvalid || isDateFromMissing}
                    helperText={isDateFromMissing ? "From is required" : undefined}
                    data-testid={`${testIdPrefix}.date-from-field`}
                  />
                </Box>
                <Box className='flex-1'>
                  <AerosDatePicker
                    label='To'
                    required={isDateToRequired}
                    value={watch("dateTo") ?? null}
                    onChange={handleDateToChange}
                    error={isDateRangeInvalid || isDateToMissing}
                    helperText={isDateToMissing ? "To is required" : undefined}
                    data-testid={`${testIdPrefix}.date-to-field`}
                  />
                </Box>
              </Box>
              {isDateRangeInvalid && <FormHelperText error>From date must be before To date</FormHelperText>}
            </Box>
          </Box>
        </Box>
      </Box>
    </SearchFilterDrawer>
  )
}
